import {
  DdlResult,
  getReportTemplateDdlResult,
  GetReportTemplateResult,
  Operator,
  ReportTemplateTypeEnum,
} from "../../../../services/api/types/reports/reports";
import {
  DateOperator,
  ListOperator,
} from "../../../../services/api/types/users/users";

// This is helper for transforming the local state format into the backend needed format
// Local state format is different for easier code reading. All the state is separated depending the section from the form
// eg: configuration: {state for the configuration section etc..}
export const transformReportTemplate = (
  data: any,
  ddl: getReportTemplateDdlResult,
  reportTemplateUid?: string,
) => {
  const result = {
    name: data.reportConfiguration.reportName,
    type: data.reportConfiguration.reportType,
    groupByPortal: data.portalSelection.groupResultsByPortal,
    actionOnUser:
      data.reportConfiguration.reportType ===
      ReportTemplateTypeEnum.USER_ACTIVITY_REPORT
        ? data.userSpecificFilters.filterByTargetUser ?? false
        : false,
    filters: [] as Array<{
      filterGroup: string;
      attribute: string;
      operator: Operator | "";
      value: string;
    }>,
    reportTemplateUid: reportTemplateUid,
  };

  // -----------------------------
  // NORMALIZATION HELPER
  // -----------------------------
  const normalize = (value: any): string => {
    if (!value) return "";

    // array - pipe
    if (Array.isArray(value)) return value.join("|");

    // comma separated → pipe
    if (typeof value === "string" && value.includes(",")) {
      return value
        .split(",")
        .map((v) => v.trim())
        .join("|");
    }

    // already pipe - leave as is
    if (typeof value === "string" && value.includes("|")) {
      return value;
    }

    return String(value);
  };

  // -----------------------------
  // MULTI-SELECT HELPER
  // -----------------------------
  const addMultiSelect = (
    keys: string[],
    section: DdlResult | null | undefined,
  ) => {
    if (!keys || keys.length === 0 || !section) return;

    result.filters.push({
      filterGroup: section.filterGroup,
      attribute: section.attributeKey,
      operator: ListOperator.IN,
      value: keys.join("|"),
    });
  };

  // -----------------------------
  // DATE HELPER
  // -----------------------------
  const pushDate = (
    df: any,
    section: { attributeKey: string; filterGroup: string },
  ) => {
    if (df.operator === DateOperator.BETWEEN) {
      if (df.startDate && df.endDate) {
        result.filters.push({
          filterGroup: section.filterGroup,
          attribute: section.attributeKey,
          operator: DateOperator.BETWEEN,
          value: `${df.startDate}|${df.endDate}`,
        });
      }
    } else if (df.date) {
      result.filters.push({
        filterGroup: section.filterGroup,
        attribute: section.attributeKey,
        operator: df.operator,
        value: df.date,
      });
    }
  };

  // -----------------------------
  // PORTAL SELECTION
  // -----------------------------
  if (data.portalSelection.portals.length > 0) {
    result.filters.push({
      filterGroup: "userPortal",
      attribute: "PortalUid",
      operator: ListOperator.IN,
      value: data.portalSelection.portals.join("|"),
    });
  }

  // -----------------------------
  // USER FILTERS (single value)
  // -----------------------------
  data.userSpecificFilters.userFilters.forEach((f: any) => {
    if (!f.attribute || !f.operator || !f.value) return;

    // find backend attribute info
    const backendAttr = ddl.userAttribute?.find(
      (attr: any) => attr.attributeKey === f.attribute,
    );

    if (!backendAttr) return;

    result.filters.push({
      filterGroup: backendAttr.filterGroup || "",
      attribute: backendAttr.attributeKey,
      operator: f.operator,
      value: normalize(f.value),
    });
  });

  // -----------------------------
  // MULTI-SELECTS FROM DDL
  // -----------------------------
  addMultiSelect(data.userSpecificFilters.activityType, ddl.userActionType);

  if (
    data.reportConfiguration.reportType ===
      ReportTemplateTypeEnum.USER_ACTIVITY_REPORT &&
    data.userSpecificFilters.activityDate &&
    ddl.userActionDate
  ) {
    const [startDate, endDate] =
      data.userSpecificFilters.activityDate.split("|");

    pushDate(
      {
        operator: DateOperator.BETWEEN,
        startDate,
        endDate,
      },
      {
        attributeKey: ddl.userActionDate.attributeKey,
        filterGroup: ddl.userActionDate.filterGroup,
      },
    );
  }

  addMultiSelect(data.assignmentFilters.assignments, ddl.userAssignment);
  addMultiSelect(
    data.assignmentFilters.assignmentsStatus,
    ddl.userAssignmentStatus,
  );
  addMultiSelect(data.assignmentFilters.progressStatus, ddl.progressStatus);

  // DATE mapping UI - backend attributeKey
  const dateMap: Record<string, string> = {
    deadline: "DueDate",
    startDate: "StartDate",
    completionDate: "CompletionDate",
    assignDate: "CreatedAt",
  };

  // -----------------------------
  // DATE FILTERS
  // -----------------------------
  data.assignmentFilters.dateFilters.forEach((df: any) => {
    const backendKey = dateMap[df.attribute];

    const section = ddl.userAssignmentDate?.find(
      (d: any) => d.attributeKey?.toLowerCase() === backendKey?.toLowerCase(),
    );

    if (!section) return;
    pushDate(df, section);
  });

  return result;
};

// Helper function for transforming the data from backend into the local state format for prepopulating
export const mapBackendToUI = (
  backend: GetReportTemplateResult,
  ddl: getReportTemplateDdlResult,
) => {
  // Convert pipe | values to arrays
  const toArray = (v: string | string[] | undefined | null): string[] => {
    if (!v) return [];
    if (Array.isArray(v)) return v.filter(Boolean);
    if (typeof v === "string") {
      if (!v.trim()) return [];
      return v.includes("|") ? v.split("|").filter(Boolean) : [v];
    }
    return [];
  };

  // Reverse date attribute mapping (backend - UI)
  const dateAttributeMap: Record<string, string> = {
    DueDate: "deadline",
    StartDate: "startDate",
    CompletionDate: "completionDate",
    CreatedAt: "assignDate",
  };

  const ui = {
    reportConfiguration: {
      reportType: backend.type || "",
      reportName: backend.name || "",
    },
    portalSelection: {
      portals: [] as string[],
      groupResultsByPortal: backend.groupByPortal ?? false,
    },
    userSpecificFilters: {
      userFilters: [] as Array<{
        attribute: string;
        operator: Operator | "";
        value: string;
      }>,
      status: [] as string[],
      activityType: [] as string[],
      activityDate: "",
      filterByTargetUser: backend.actionOnUser ?? false,
    },
    assignmentFilters: {
      assignments: [] as string[],
      assignmentsStatus: [] as string[],
      progressStatus: [] as string[],
      dateFilters: [] as Array<{
        attribute: string;
        operator: Operator | "";
        date: string;
        startDate: string;
        endDate: string;
      }>,
    },
  };

  backend.filters?.forEach((f) => {
    const { filterGroup, attribute, operator, value } = f;
    const arrVal = toArray(value);

    // -----------------------
    // PORTALS
    // -----------------------
    if (filterGroup === "userPortal" && attribute === "PortalUid") {
      ui.portalSelection.portals = arrVal;
      return;
    }

    // -----------------------
    // USER FILTERS (attributes)
    // -----------------------
    const backendAttr = ddl.userAttribute?.find(
      (a) => a?.attributeKey === attribute && a?.filterGroup === filterGroup,
    );

    if (backendAttr) {
      ui.userSpecificFilters.userFilters.push({
        attribute: attribute,
        operator: operator || "",
        value: Array.isArray(value) ? value.join("|") : String(value || ""),
      });
      return;
    }

    // -----------------------
    // USER FILTERS (activity type)
    // -----------------------
    if (
      ddl.userActionType &&
      ddl.userActionType.attributeKey === attribute &&
      ddl.userActionType.filterGroup === filterGroup
    ) {
      ui.userSpecificFilters.activityType = arrVal;
      return;
    }

    // -----------------------
    // USER ACTIVITY DATE RANGE
    // -----------------------
    if (
      ddl.userActionDate &&
      ddl.userActionDate.attributeKey === attribute &&
      ddl.userActionDate.filterGroup === filterGroup
    ) {
      ui.userSpecificFilters.activityDate = arrVal.join("|");
      return;
    }

    // -----------------------
    // ASSIGNMENT FILTERS
    // -----------------------
    if (
      ddl.userAssignment &&
      ddl.userAssignment.attributeKey === attribute &&
      ddl.userAssignment.filterGroup === filterGroup
    ) {
      ui.assignmentFilters.assignments = arrVal;
      return;
    }

    // -----------------------
    // ASSIGNMENT STATUS
    // -----------------------
    if (
      ddl.userAssignmentStatus &&
      ddl.userAssignmentStatus.attributeKey === attribute &&
      ddl.userAssignmentStatus.filterGroup === filterGroup
    ) {
      ui.assignmentFilters.assignmentsStatus = arrVal;
      return;
    }

    // -----------------------
    // PROGRESS STATUS
    // -----------------------
    if (
      ddl.progressStatus &&
      ddl.progressStatus.attributeKey === attribute &&
      ddl.progressStatus.filterGroup === filterGroup
    ) {
      ui.assignmentFilters.progressStatus = arrVal;
      return;
    }

    // -----------------------
    // DATE FILTERS
    // -----------------------
    const dateSection = ddl.userAssignmentDate?.find(
      (d) =>
        d &&
        d.attributeKey?.toLowerCase() === attribute.toLowerCase() &&
        d.filterGroup === filterGroup,
    );

    if (dateSection) {
      const uiAttribute =
        dateAttributeMap[attribute] || attribute.toLowerCase();

      if (operator === DateOperator.BETWEEN) {
        ui.assignmentFilters.dateFilters.push({
          attribute: uiAttribute,
          operator,
          startDate: arrVal[0] || "",
          endDate: arrVal[1] || "",
          date: "",
        });
      } else {
        ui.assignmentFilters.dateFilters.push({
          attribute: uiAttribute,
          operator,
          date: arrVal[0] || "",
          startDate: "",
          endDate: "",
        });
      }
      return;
    }
  });

  // Default rows if empty
  if (ui.userSpecificFilters.userFilters.length === 0) {
    ui.userSpecificFilters.userFilters = [
      { attribute: "", operator: "", value: "" },
    ];
  }

  if (ui.assignmentFilters.dateFilters.length === 0) {
    ui.assignmentFilters.dateFilters = [
      { attribute: "", operator: "", date: "", startDate: "", endDate: "" },
    ];
  }

  return ui;
};
